// CustomPropertyProvenanceChip — FR #198, option B (drthe0, 2026-06-02).
//
// Meta row rendered UNDER each populated custom property value, answering
// "where did this value come from?" without leaving the document detail
// page. Bare source label pill + muted "set 3m ago by chadwiki@" text.
//
// Visual: small 2-char source pill (op/wf/in/sc/?) followed by a quiet
// muted "set <relative> by <actor>" line. Hover surfaces the absolute
// timestamp + raw audit action. Properties that have never been set
// show nothing — the chip is omitted entirely.

import type { CustomPropertyProvenanceEntry, CustomPropertySource } from '@captain-tasko/shared';

interface SourceMeta {
  label: string;
  /** Used in the "set N ago at <source>" suffix and in the tooltip. */
  blurb: string;
  bg: string;
  fg: string;
  border: string;
}

const SOURCE_META: Record<CustomPropertySource, SourceMeta> = {
  operator: {
    label: 'op',
    blurb: 'by',
    bg: 'color-mix(in srgb, var(--accent-primary) 14%, var(--surface-raised))',
    fg: 'var(--accent-primary)',
    border: 'color-mix(in srgb, var(--accent-primary) 40%, transparent)',
  },
  workflow: {
    label: 'wf',
    blurb: 'by workflow',
    bg: 'color-mix(in srgb, var(--success-primary) 14%, var(--surface-raised))',
    fg: 'var(--success-primary)',
    border: 'color-mix(in srgb, var(--success-primary) 40%, transparent)',
  },
  ingest: {
    label: 'in',
    blurb: 'at ingest',
    bg: 'color-mix(in srgb, #8B5CF6 14%, var(--surface-raised))',
    fg: '#8B5CF6',
    border: 'color-mix(in srgb, #8B5CF6 40%, transparent)',
  },
  sidecar: {
    label: 'sc',
    blurb: 'by sidecar pipeline',
    bg: 'color-mix(in srgb, #F59E0B 14%, var(--surface-raised))',
    fg: '#F59E0B',
    border: 'color-mix(in srgb, #F59E0B 40%, transparent)',
  },
  unknown: {
    label: '?',
    blurb: 'pre-tracking',
    bg: 'var(--surface-sunken)',
    fg: 'var(--text-muted)',
    border: 'var(--border-subtle)',
  },
};

function formatRelative(iso: string, now = Date.now()): string {
  let dt: number;
  try { dt = new Date(iso).getTime(); } catch { return ''; }
  if (Number.isNaN(dt)) return '';
  const seconds = Math.max(0, Math.round((now - dt) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

function formatAbsolute(iso: string): string {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export interface CustomPropertyProvenanceChipProps {
  entry: CustomPropertyProvenanceEntry;
}

export function CustomPropertyProvenanceChip({ entry }: CustomPropertyProvenanceChipProps) {
  const meta = SOURCE_META[entry.source] ?? SOURCE_META.unknown;
  const rel = formatRelative(entry.at);
  const abs = formatAbsolute(entry.at);
  const actorDisplay = entry.actorName ?? (entry.actorId ? entry.actorId : 'system');
  // Tagline shape depends on source. Operator-set values name the
  // person; pipeline-set values name the path.
  let tagline: string;
  if (entry.source === 'operator') {
    tagline = `set ${rel} by ${actorDisplay}`;
  } else if (entry.source === 'unknown') {
    tagline = `set ${rel} (source unknown)`;
  } else {
    tagline = `set ${rel} ${meta.blurb}`;
  }

  const tooltipLines = [
    `Source: ${entry.source}`,
    `Updated ${rel} (${abs})`,
    `By ${actorDisplay}`,
  ];
  if (entry.auditAction) tooltipLines.push(`Audit action: ${entry.auditAction}`);

  return (
    <div
      title={tooltipLines.join('\n')}
      aria-label={`Source: ${entry.source}, ${tagline}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 4,
        fontSize: 10,
        color: 'var(--text-muted)',
        cursor: 'help',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '1px 5px',
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
          background: meta.bg,
          color: meta.fg,
          border: `1px solid ${meta.border}`,
          lineHeight: 1.3,
        }}
      >
        {meta.label}
      </span>
      <span>{tagline}</span>
    </div>
  );
}
